import { css, html, nothing, svg, type TemplateResult } from "lit";
import type { Frame } from "./board/field";
import { ARENA_SYMBOLS } from "./board/mana-pips";

export type FaceArt = { src: string; failed: () => void } | null;

export type CardFace = { name: string; frame: Frame; art: FaceArt; manaCost: string; typeLine: string; text: string; pt: string };

export type AbilityFace = { source: string; frame: Frame; art: FaceArt; kind: string; text: string; loyalty: string };

export type WalkerFace = { name: string; frame: Frame; art: FaceArt; loyalty: string };

type Palette = { body: string; streak: string; grain: string; streakOpacity: number; speck: string; box: [string, string]; text: string; pin: [string, string] };

const PALETTES: Record<Frame, Palette> = {
  W: { body: "#d9d2b8", streak: "#fbf8ea", grain: "0.02 0.035", streakOpacity: 0.5, speck: "#8c8466", box: ["#f9f6ea", "#e3dcc4"], text: "#f8f6ef", pin: ["#c9c0a0", "#c9c0a0"] },
  U: { body: "#2a6bb0", streak: "#b6d4f0", grain: "0.02 0.035", streakOpacity: 0.4, speck: "#123a66", box: ["#e1ebf4", "#bed1e3"], text: "#edf2f7", pin: ["#2c75c4", "#2c75c4"] },
  B: { body: "#2c2826", streak: "#8a827c", grain: "0.02 0.035", streakOpacity: 0.4, speck: "#000000", box: ["#d9d3cf", "#b9b0aa"], text: "#e9e5e2", pin: ["#1a1716", "#1a1716"] },
  R: { body: "#c73a24", streak: "#fdeee8", grain: "0.045 0.055", streakOpacity: 0.95, speck: "#6d140a", box: ["#f7e9e5", "#e6cdc6"], text: "#f9f0ee", pin: ["#d2422a", "#d2422a"] },
  G: { body: "#2b5a3a", streak: "#86ad80", grain: "0.02 0.035", streakOpacity: 0.4, speck: "#0c2614", box: ["#e6ebdf", "#c9d4bf"], text: "#e9ebe4", pin: ["#3d8150", "#3d8150"] },
  gold: { body: "#c4a058", streak: "#f6e7b6", grain: "0.018 0.03", streakOpacity: 0.5, speck: "#5c4416", box: ["#efe2bb", "#d8c288"], text: "#f4f1ee", pin: ["#b8913f", "#b8913f"] },
  colorless: { body: "#9aa0a5", streak: "#f1f4f6", grain: "0.02 0.035", streakOpacity: 0.45, speck: "#3f454a", box: ["#eceff1", "#c9cfd4"], text: "#f2f3f4", pin: ["#7c848b", "#7c848b"] },
};

const RIM = "#1f1813";
const CURVE_START = 715;

function outline(x0: number, y0: number, x1: number, y1: number, r: number, curved: boolean, curveFrom = CURVE_START): string {
  const top = `M${x0} ${y0 + r} A${r} ${r} 0 0 1 ${x0 + r} ${y0} H${x1 - r} A${r} ${r} 0 0 1 ${x1} ${y0 + r}`;
  if (!curved) return `${top} V${y1 - r} A${r} ${r} 0 0 1 ${x1 - r} ${y1} H${x0 + r} A${r} ${r} 0 0 1 ${x0} ${y1 - r} Z`;
  const cx = (x0 + x1) / 2, rx = (x1 - x0) / 2, ry = y1 - curveFrom, k = 0.5523;
  return `${top} V${curveFrom} C${x1} ${curveFrom + k * ry} ${cx + k * rx} ${y1} ${cx} ${y1} C${cx - k * rx} ${y1} ${x0} ${curveFrom + k * ry} ${x0} ${curveFrom} Z`;
}

function bar(x: number, y: number, w: number, h: number, r: number): TemplateResult {
  return svg`
    <rect x=${x - 6.5} y=${y - 6.5} width=${w + 13} height=${h + 13} rx=${r + 5} fill=${RIM}/>
    <rect x=${x - 4.5} y=${y - 4.5} width=${w + 9} height=${h + 9} rx=${r + 3.5} fill="url(#face-pin)"/>
    <rect x=${x - 4.5} y=${y - 4.5} width=${w + 9} height=${h + 9} rx=${r + 3.5} fill="url(#face-pin-shade)"/>
    <rect x=${x - 1.5} y=${y - 1.5} width=${w + 3} height=${h + 3} rx=${r + 1} fill=${RIM}/>
    <rect x=${x} y=${y} width=${w} height=${h} rx=${r} fill="url(#face-bar)"/>
    <rect x=${x} y=${y} width=${w} height=${h} rx=${r} fill="url(#face-bar-shade)"/>
    <rect x=${x + 1} y=${y + 1} width=${w - 2} height=${h - 2} rx=${r - 1} fill="none" stroke="#fff" stroke-opacity="0.55" stroke-width="1.5"/>`;
}

function frameDefs(p: Palette): TemplateResult {
  return svg`
    <linearGradient id="face-pin" x1="0" x2="1"><stop offset="0.48" stop-color=${p.pin[0]}/><stop offset="0.52" stop-color=${p.pin[1]}/></linearGradient>
    <linearGradient id="face-pin-shade" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fff" stop-opacity="0.35"/><stop offset="0.5" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity="0.3"/></linearGradient>
    <linearGradient id="face-bar" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color=${p.box[0]}/><stop offset="1" stop-color=${p.box[1]}/></linearGradient>
    <linearGradient id="face-bar-shade" x1="0" x2="1"><stop offset="0" stop-color="#000" stop-opacity="0.12"/><stop offset="0.1" stop-color="#000" stop-opacity="0"/><stop offset="0.9" stop-color="#000" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity="0.12"/></linearGradient>
    <filter id="face-streak" x="0" y="0" width="1" height="1"><feTurbulence type="fractalNoise" baseFrequency=${p.grain} numOctaves="4" seed="7"/><feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 3.6 0 0 -1.75"/><feComposite in2="SourceGraphic" operator="in"/></filter>
    <filter id="face-speck" x="0" y="0" width="1" height="1"><feTurbulence type="fractalNoise" baseFrequency="0.09" numOctaves="2" seed="3"/><feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 4 0 -2.4"/><feComposite in2="SourceGraphic" operator="in"/></filter>
    <filter id="face-paper" x="0" y="0" width="1" height="1"><feTurbulence type="fractalNoise" baseFrequency="0.6" numOctaves="2" seed="5"/><feColorMatrix type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.07 0"/><feComposite in2="SourceGraphic" operator="in"/></filter>
    <filter id="face-shade" x="-0.05" y="-0.05" width="1.1" height="1.1"><feGaussianBlur stdDeviation="1.2"/></filter>`;
}

function frameSvg(frame: Frame, curved: boolean, ptBox: boolean): TemplateResult {
  const p = PALETTES[frame];
  const deep = (normal: number, rounded: number) => curved ? rounded : normal;
  return html`<svg class="frame" viewBox="0 0 672 936" preserveAspectRatio="none" aria-hidden="true">
    <defs>
      ${frameDefs(p)}
      <clipPath id="face-body"><path d=${outline(27, 27, 645, deep(870, 909), 16, curved)}/></clipPath>
    </defs>
    <path d=${outline(0, 0, 672, 936, 30, curved)} fill="#171411"/>
    <g clip-path="url(#face-body)">
      <rect x="27" y="27" width="618" height="882" fill=${p.body}/>
      <rect x="27" y="27" width="618" height="882" fill=${p.streak} filter="url(#face-streak)" opacity=${p.streakOpacity}/>
      <rect x="27" y="27" width="618" height="882" fill=${p.speck} filter="url(#face-speck)" opacity="0.55"/>
      <path d=${outline(28, 28, 644, deep(869, 908), 15, curved)} fill="none" stroke="#fff" stroke-opacity="0.35" stroke-width="2"/>
    </g>
    <rect x="39.5" y="97" width="585.5" height="437" fill="#000" opacity="0.7" filter="url(#face-shade)"/>
    <rect x="43.5" y="96" width="587" height="438" fill="url(#face-pin)"/>
    <rect x="49" y="99" width="574" height="432" fill=${RIM}/>
    <path d=${outline(39.5, 584, 625, deep(872, 891), 0, curved)} fill="#000" opacity="0.7" filter="url(#face-shade)"/>
    <path d=${outline(43.5, 583.5, 630, deep(873.5, 892.5), 0, curved)} fill="url(#face-pin)"/>
    <path d=${outline(49, 589, 623, deep(868, 887), 0, curved)} fill=${p.text}/>
    <path d=${outline(49, 589, 623, deep(868, 887), 0, curved)} fill="#000" filter="url(#face-paper)"/>
    ${bar(40, 43, 592, 50, 11)}
    ${bar(40, 529, 592, 46, 11)}
    ${ptBox ? bar(526, 837, 108, 46, 9) : nothing}
  </svg>`;
}

function walkerFrameSvg(frame: Frame, art: FaceArt): TemplateResult {
  const p = PALETTES[frame];
  return html`<svg class="frame" viewBox="0 0 672 936" preserveAspectRatio="none" aria-hidden="true">
    <defs>
      ${frameDefs(p)}
      <clipPath id="walker-frame"><path d=${outline(26, 26, 646, 886, 20, true, 700)}/></clipPath>
      <clipPath id="walker-art"><path d=${outline(52, 122, 620, 848, 10, true, 690)}/></clipPath>
    </defs>
    <g clip-path="url(#walker-frame)">
      <rect x="26" y="26" width="620" height="860" fill=${p.body}/>
      <rect x="26" y="26" width="620" height="860" fill=${p.streak} filter="url(#face-streak)" opacity=${p.streakOpacity}/>
      <rect x="26" y="26" width="620" height="860" fill=${p.speck} filter="url(#face-speck)" opacity="0.55"/>
      <path d=${outline(27, 27, 645, 885, 19, true, 700)} fill="none" stroke="#fff" stroke-opacity="0.35" stroke-width="2"/>
    </g>
    <path d=${outline(44, 116, 628, 856, 16, true, 690)} fill="url(#face-pin)"/>
    <path d=${outline(48, 118, 624, 852, 13, true, 690)} fill=${RIM}/>
    <g clip-path="url(#walker-art)">
      <rect x="52" y="122" width="568" height="726" fill="#2c2c2c"/>
      ${art ? svg`<image href=${art.src} x="-349.5" y="-69.1" width="1371.1" height="1910.5" preserveAspectRatio="none" @error=${art.failed}/>` : nothing}
    </g>
    ${bar(44, 40, 584, 60, 12)}
  </svg>`;
}

const SYMBOL_IMAGES = new Set([...ARENA_SYMBOLS, "T", "E", "S", "P"]);

function symbol(token: string): TemplateResult | string {
  const parts = token.slice(1, -1).split("/");
  const key = parts.filter((p) => p !== "P").sort().join("") + (parts.includes("P") ? "P" : "");
  const numeric = /^\d+$/.test(key) && Number(key) <= 20;
  return numeric || SYMBOL_IMAGES.has(key) ? html`<img class="pip" src="./symbols/mana/${key}.png" alt=${token} draggable="false">` : token;
}

function withSymbols(text: string): (TemplateResult | string)[] {
  return text.split(/(\{[^{}]+\})/g).filter((part) => part !== "").map((part) => part.startsWith("{") ? symbol(part) : part);
}

function printedWording(text: string, name: string): string {
  return text.replace(/\b[Tt]his (creature|Aura|enchantment|artifact|land|spell)\b/g, name);
}

export function rulesText(text: string, name: string): TemplateResult[] {
  return text.split("\n").filter((line) => line.trim() !== "").map((line) => html`<p>${line.split(/(\([^)]*\))/g).filter((part) => part !== "")
    .map((part) => part.startsWith("(") ? html`<i>${withSymbols(part)}</i>` : withSymbols(printedWording(part, name)))}</p>`);
}

function artWindow(art: FaceArt): TemplateResult {
  return html`<div class="art">${art ? html`<img src=${art.src} alt="" draggable="false" @error=${art.failed}>` : nothing}</div>`;
}

export function renderCardFace(c: CardFace): TemplateResult {
  return html`<div class="face" role="img" aria-label=${c.name}>
    ${frameSvg(c.frame, false, c.pt !== "")}
    ${artWindow(c.art)}
    <div class="name"><span class="line">${c.name}</span><span class="cost">${withSymbols(c.manaCost)}</span></div>
    <div class="type"><span class="line">${c.typeLine}</span></div>
    <div class="box"><div class="text">${rulesText(c.text, c.name)}</div></div>
    ${c.pt !== "" ? html`<div class="pt"><span>${c.pt}</span></div>` : nothing}
  </div>`;
}

function loyaltyShape(loyalty: string): string {
  return loyalty.startsWith("+") ? "up" : /^[−-]/.test(loyalty) ? "down" : "flat";
}

export function renderAbilityFace(a: AbilityFace): TemplateResult {
  return html`<div class="face ability" role="img" aria-label="${a.source} ${a.kind.toLowerCase()}: ${a.loyalty ? `${a.loyalty}: ` : ""}${a.text}">
    ${frameSvg(a.frame, true, false)}
    ${artWindow(a.art)}
    <div class="name"><span class="line">${a.source}</span></div>
    <div class="type"><span class="line">${a.kind}</span></div>
    <div class="box ${a.loyalty ? "with-loyalty" : ""}">
      ${a.loyalty ? html`<span class="loyalty ${loyaltyShape(a.loyalty)}"><span>${a.loyalty}</span></span>` : nothing}
      <div class="text">${rulesText(a.text, a.source)}</div>
    </div>
  </div>`;
}

export function renderWalkerFace(w: WalkerFace): TemplateResult {
  return html`<div class="face walker" role="img" aria-label="${w.name}, loyalty ${w.loyalty}">
    ${walkerFrameSvg(w.frame, w.art)}
    <div class="name"><span class="line">${w.name}</span></div>
    <div class="shield"><span>${w.loyalty}</span></div>
  </div>`;
}

const fontSize = (el: HTMLElement, units: number): void => { el.style.fontSize = `calc(var(--u) * ${units})`; };

function shrinkUntil(el: HTMLElement, fits: () => boolean, from: number, smallest: number): void {
  let units = from;
  fontSize(el, units);
  while (!fits() && units > smallest) {
    units -= 0.5;
    fontSize(el, units);
  }
}

export function fitCardFace(face: HTMLElement): void {
  const u = face.offsetWidth / 672;
  if (u === 0) return;
  const walker = face.classList.contains("walker");
  for (const [selector, from, smallest] of [[".name .line", walker ? 40 : 33, 20], [".type .line", 28, 18]] as const) {
    const line = face.querySelector<HTMLElement>(selector);
    if (line) shrinkUntil(line, () => line.scrollWidth <= line.clientWidth, from, smallest);
  }
  const box = face.querySelector<HTMLElement>(".box");
  const text = face.querySelector<HTMLElement>(".text");
  if (box && text) {
    const room = face.classList.contains("ability") ? 150 * u : box.clientHeight - 30 * u;
    shrinkUntil(text, () => text.scrollHeight <= room, 32.5, 18);
  }
  const pt = face.querySelector<HTMLElement>(".pt span");
  if (pt) shrinkUntil(pt, () => pt.offsetWidth <= 98 * u, 33.3, 20);
}

export function frameOfManaCost(cost: string): Frame {
  const colors = new Set(cost.match(/[WUBRG]/g) ?? []);
  if (colors.size > 1) return "gold";
  const [only] = colors;
  return (only as Frame | undefined) ?? "colorless";
}

export const cardFaceStyles = css`
  .card.drawn { background: none; box-shadow: none; overflow: visible; border-radius: 0; }
  .face {
    --u: calc(var(--card-w, 110px) / 672);
    position: absolute; left: 0; top: 0; width: calc(var(--u) * 672); height: calc(var(--u) * 936); color: #111;
    filter: drop-shadow(0 1px 2px rgb(0 0 0 / 0.55));
  }
  .face svg.frame { position: absolute; inset: 0; width: 100%; height: 100%; }
  .face .art {
    position: absolute; left: calc(var(--u) * 51); top: calc(var(--u) * 101); width: calc(var(--u) * 570); height: calc(var(--u) * 428);
    overflow: hidden; background: linear-gradient(160deg, #3a3a3a, #232323);
  }
  .face .art img {
    position: absolute; left: calc(var(--u) * -68.1); top: calc(var(--u) * -114.6); width: calc(var(--u) * 705.6); height: calc(var(--u) * 982.8);
    max-width: none; object-fit: fill;
  }
  .face .name, .face .type {
    position: absolute; left: calc(var(--u) * 58); right: calc(var(--u) * 46); display: flex; align-items: center; justify-content: space-between;
    gap: calc(var(--u) * 8); font-family: "Onthestack Title", Georgia, serif; font-weight: 700; white-space: nowrap; color: #2b2622;
  }
  .face .line { min-width: 0; overflow: hidden; line-height: 1.2; }
  .face .name { top: calc(var(--u) * 43); height: calc(var(--u) * 50); font-size: calc(var(--u) * 33); }
  .face .type { top: calc(var(--u) * 527.5); height: calc(var(--u) * 46); font-size: calc(var(--u) * 28); }
  .face .cost { flex: none; display: flex; gap: calc(var(--u) * 1.5); filter: drop-shadow(0 calc(var(--u) * 2.5) calc(var(--u) * 2.5) #000b); }
  .face .cost .pip { width: calc(var(--u) * 34); height: calc(var(--u) * 34); }
  .face .box {
    position: absolute; left: calc(var(--u) * 49); top: calc(var(--u) * 589); width: calc(var(--u) * 574); height: calc(var(--u) * 279);
    box-sizing: border-box; padding: calc(var(--u) * 10) calc(var(--u) * 11) calc(var(--u) * 12); display: flex; flex-direction: column; justify-content: center;
  }
  .face .text {
    font-family: "Onthestack Text", Georgia, serif; font-size: calc(var(--u) * 32.5); line-height: 1.04;
    font-feature-settings: "liga" 1, "lnum" 1; overflow-wrap: break-word;
  }
  .face .text p { margin: 0 0 0.3em; }
  .face .text p:last-child { margin-bottom: 0; }
  .face .text .pip { display: inline-block; width: 0.9em; height: 0.9em; vertical-align: -0.1em; margin: 0 0.02em; }
  .face .pt {
    position: absolute; left: calc(var(--u) * 526); top: calc(var(--u) * 837); width: calc(var(--u) * 108); height: calc(var(--u) * 46);
    display: grid; place-items: center; box-sizing: border-box; padding-top: calc(var(--u) * 2);
    font: 700 calc(var(--u) * 33.3)/1 "Onthestack Title", Georgia, serif; white-space: nowrap;
  }
  .face .pt span { display: inline-block; }
  .face.ability .box { height: calc(var(--u) * 262); padding-left: calc(var(--u) * 24); padding-right: calc(var(--u) * 24); }
  .face.ability .text { text-align: center; text-wrap: balance; }
  .face.ability .box.with-loyalty { padding-left: calc(var(--u) * 118); }
  .face .loyalty {
    position: absolute; left: calc(var(--u) * 22); top: 50%; transform: translateY(-50%);
    display: grid; place-items: center; width: calc(var(--u) * 84); height: calc(var(--u) * 72);
    background: #f2eee5; color: #fff; font: 700 calc(var(--u) * 34)/1 "Onthestack Title", Georgia, serif;
  }
  .face .loyalty::before { content: ""; position: absolute; inset: calc(var(--u) * 3); background: #17191d; clip-path: inherit; }
  .face .loyalty > span { position: relative; text-shadow: 0 1px 2px #000; }
  .face .loyalty.up { clip-path: polygon(50% 0, 100% 26%, 100% 100%, 0 100%, 0 26%); padding-top: calc(var(--u) * 10); box-sizing: border-box; }
  .face .loyalty.down { clip-path: polygon(0 0, 100% 0, 100% 74%, 50% 100%, 0 74%); padding-bottom: calc(var(--u) * 10); box-sizing: border-box; }
  .face .loyalty.flat { clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%); }
  .face.walker .name { top: calc(var(--u) * 40); height: calc(var(--u) * 60); left: calc(var(--u) * 62); right: calc(var(--u) * 58); font-size: calc(var(--u) * 40); }
  .face.walker .shield {
    position: absolute; left: 50%; bottom: calc(var(--u) * 4); transform: translateX(-50%);
    width: 2.7em; height: 1.7em; box-sizing: border-box; padding-bottom: 0.22em; display: grid; place-items: center;
    background: #ece8df; clip-path: polygon(12% 0, 88% 0, 100% 20%, 100% 62%, 50% 100%, 0 62%, 0 20%);
    color: #fff; font: 700 max(calc(var(--u) * 70), 10px)/1 "Onthestack Title", Georgia, serif;
  }
  .face.walker .shield::before { content: ""; position: absolute; inset: max(calc(var(--u) * 6), 1.5px); background: #141416; clip-path: inherit; }
  .face.walker .shield > span { position: relative; text-shadow: 0 1px 2px #000; }
`;
